/**
 * Unified Dashboard Page
 * Adapts based on user role with role-specific tabs
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import AppLayout from '../components/common/AppLayout';
import TabNavigation from '../components/common/TabNavigation';
import { getTabsForRole, getRoleSubtitle, isRoleInCategory } from '../components/dashboard/config/dashboardConfig';
import { hasDirectorAccess } from '../utils/rolePermissions';

// Tab components
import OverviewTab from '../components/dashboard/tabs/OverviewTab';
import ProjectsTab from '../components/dashboard/tabs/ProjectsTab';
import MeasurementsTab from '../components/dashboard/tabs/MeasurementsTab';
import UsersTab from '../components/dashboard/tabs/UsersTab';
import LeadsTab from '../components/dashboard/tabs/LeadsTab';
import DistributionTab from '../components/dashboard/tabs/DistributionTab';
import MyLeadsTab from '../components/dashboard/tabs/MyLeadsTab';
import MyProjectsTab from '../components/dashboard/tabs/MyProjectsTab';
import MyMeasurementsTab from '../components/dashboard/tabs/MyMeasurementsTab';
import My2DTasksTab from '../components/dashboard/tabs/My2DTasksTab';
import My3DTasksTab from '../components/dashboard/tabs/My3DTasksTab';
import MyDDTasksTab from '../components/dashboard/tabs/MyDDTasksTab';
import MyQuotationTasksTab from '../components/dashboard/tabs/MyQuotationTasksTab';
import SiteDatabaseTab from '../components/dashboard/tabs/SiteDatabaseTab';
import SiteAttendanceTrackerTab from '../components/dashboard/tabs/SiteAttendanceTrackerTab';
import SiteAttendanceMapTab from '../components/dashboard/tabs/SiteAttendanceMapTab';
import ImportedDataMappingTab from '../components/dashboard/tabs/ImportedDataMappingTab';
import PaymentAnalyticsTab from '../components/dashboard/tabs/PaymentAnalyticsTab';

// Map tab IDs to components
const TAB_COMPONENTS = {
  'overview': OverviewTab,
  'projects': ProjectsTab,
  'measurements': MeasurementsTab,
  'users': UsersTab,
  'leads': LeadsTab,
  'distribution': DistributionTab,
  'my-leads': MyLeadsTab,
  'my-projects': MyProjectsTab,
  'my-measurements': MyMeasurementsTab,
  'my-2d-tasks': My2DTasksTab,
  'my-3d-tasks': My3DTasksTab,
  'my-dd-tasks': MyDDTasksTab,
  'my-quotation-tasks': MyQuotationTasksTab,
  'site-database': SiteDatabaseTab,
  'site-attendance-tracker': SiteAttendanceTrackerTab,
  'site-attendance-map': SiteAttendanceMapTab,
  'imported-data-mapping': ImportedDataMappingTab,
  'payment-analytics': PaymentAnalyticsTab
};

export default function Dashboard() {
  const { user } = useAuth();
  const { fetchMyProjects } = useProjects();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = user?.role;

  // Get tabs for this role
  const tabs = getTabsForRole(role);

  // Read initial tab from URL query param or default to first tab
  const tabFromUrl = searchParams.get('tab');
  const defaultTab = tabs[0]?.id || 'overview';
  const initialTab = tabFromUrl && tabs.find(t => t.id === tabFromUrl) ? tabFromUrl : defaultTab;

  // Initialize active tab
  const [activeTab, setActiveTab] = useState(initialTab);

  // Handle tab changes - update both state and URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Update URL with new tab (without adding history entries)
    setSearchParams({ tab: newTab }, { replace: true });
  };

  // Sync with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && tabs.find(t => t.id === tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
  }, [searchParams, tabs, activeTab]);

  // Fetch projects for design-related roles
  useEffect(() => {
    if (isRoleInCategory(role, 'DESIGN_LEAD') ||
        isRoleInCategory(role, 'DESIGN_WORKER') ||
        isRoleInCategory(role, 'LEGAL') ||
        hasDirectorAccess(role) ||
        role === 'manager' ||
        role === 'leader') {
      fetchMyProjects();
    }
  }, [role, fetchMyProjects]);

  // Update active tab when role changes
  useEffect(() => {
    const newTabs = getTabsForRole(role);
    if (newTabs.length > 0 && !newTabs.find(t => t.id === activeTab)) {
      setActiveTab(newTabs[0].id);
    }
  }, [role, activeTab]);

  // Get the active tab component
  const ActiveTabComponent = TAB_COMPONENTS[activeTab];
  const subtitle = getRoleSubtitle(role);

  // Site draughter has special layout without standard header
  const isSiteDraughter = role === 'draughter_site';

  if (isSiteDraughter) {
    return (
      <AppLayout>
        <SiteDraughterLayout
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          ActiveTabComponent={ActiveTabComponent}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Welcome Header */}
      <div className="page-header">
        <h1 className="page-title">
          Welcome, {user?.nickname || user?.fullName}
        </h1>
        <p className="page-subtitle">{subtitle}</p>
      </div>

      {/* Tab Navigation */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onChange={handleTabChange}
      />

      {/* Tab Content */}
      {ActiveTabComponent && <ActiveTabComponent />}
    </AppLayout>
  );
}

/**
 * Special layout for Site Draughter
 * Maintains the original SiteDraughterDashboard styling
 */
function SiteDraughterLayout({ tabs, activeTab, setActiveTab, ActiveTabComponent }) {
  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Site Measurement Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">
            View and manage your assigned measurements
          </p>
        </div>
      </div>

      {/* Stats cards are rendered inside OverviewTab but site draughter doesn't have overview */}
      {/* Site draughter gets stats as part of the tab content */}
      <SiteDraughterStats />

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px overflow-x-auto scrollbar-hide snap-x snap-mandatory">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 sm:px-6 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 snap-start touch-manipulation ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6">
            {ActiveTabComponent && <ActiveTabComponent />}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Stats cards for Site Draughter
 */
function SiteDraughterStats() {
  const [stats, setStats] = useState({
    myAssigned: 0,
    myInProgress: 0,
    myCompleted: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  async function fetchStats() {
    try {
      const { measurementAPI } = await import('../services/api');
      const myRes = await measurementAPI.getMy();
      const myMeasurements = myRes.data.data || [];

      setStats({
        myAssigned: myMeasurements.filter(m => m.status === 'assigned').length,
        myInProgress: myMeasurements.filter(m => m.status === 'in_progress').length,
        myCompleted: myMeasurements.filter(m => m.status === 'completed').length
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }

  const statsConfig = [
    { key: 'myAssigned', label: 'Assigned to Me', color: 'blue', icon: 'user' },
    { key: 'myInProgress', label: 'In Progress', color: 'purple', icon: 'lightning' },
    { key: 'myCompleted', label: 'Completed', color: 'green', icon: 'check' }
  ];

  const icons = {
    clipboard: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    user: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    lightning: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    check: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  };

  const colorClasses = {
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsConfig.map((stat) => {
          const colors = colorClasses[stat.color];
          return (
            <div key={stat.key} className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center`}>
                    <div className={colors.text}>
                      {icons[stat.icon]}
                    </div>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats[stat.key]}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
